import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakService } from 'keycloak-angular';

import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { ErrorDialogComponent } from 'src/app/shared/dialogs/error-dialog/error-dialog.component';
import { SuccessDialogComponent } from 'src/app/shared/dialogs/success-dialog/success-dialog.component';
import { SystemAssetSourceService } from '../services/system-asset-source.service'
import {
  AssetManagement,
  AssetSourceConfigurationItem
} from '@aptsi-types';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WorkflowNavigationService } from '../../shared/workflow-navigation.service';



interface AssetSourceCatalogItem {
  value: string;
  label: string;
  assetSourceType: string;
  configItems: Array<{ attributeName: string; protection: boolean }>;
}

@Component({
  selector: 'app-system-asset-capture-create-or-update',
  templateUrl: './system-asset-capture-create-or-update.component.html',
  styleUrls: ['./system-asset-capture-create-or-update.component.scss']
})
export class SystemAssetCaptureCreateOrUpdateComponent implements OnInit {
  isCreate = false;
  existingAssetSourceId: string | null = null;
  organizationId = '';
  importedBy = '';

  form!: FormGroup;
  currentStep = 1;
  loading = false;
  testingConnection = false;
  importingAssets = false;

  isConnectionTestSuccessful = false;
  connectionTestMessage = '';
  connectionTestError = false;

  importChoice: 'now' | 'later' | null = null;
  importCompleted = false;
  importMessage = '';
  importedAssets: AssetManagement[] = [];
  completionMessage = '';

  showPasswordMap: boolean[] = [];


  readonly steps = [
    { number: 1, title: 'Asset source', description: 'Identify the source' },
    { number: 2, title: 'Connection', description: 'Configure and test access' },
    { number: 3, title: 'Import assets', description: 'Choose when to import' },
    { number: 4, title: 'Review', description: 'Confirm setup details' }
  ];

  readonly assetSourceTypeOptions = [
   // { value: 'cicd', label: 'CI/CD' },
    { value: 'cmdb', label: 'CMDB' }
  ];

  assetSourceOptions: Array<{ value: string; label: string }> = [];

  private readonly sourceCatalog: AssetSourceCatalogItem[] = [
    {
      value: 'jenkins',
      label: 'Jenkins',
      assetSourceType: 'cicd',
      configItems: [
        { attributeName: 'username', protection: false },
        { attributeName: 'token', protection: true },
        { attributeName: 'password', protection: true }
      ]
    },
    {
      value: 'servicenow',
      label: 'ServiceNow',
      assetSourceType: 'cmdb',
      configItems: [
        { attributeName: 'username', protection: false },
        { attributeName: 'password', protection: true }
      ]
    }
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly systemAssetSourceService: SystemAssetSourceService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly keycloakStateService: KeycloakStateService,
    private readonly keycloakService: KeycloakService,
    private readonly fb: FormBuilder,
      private readonly workflowNavigation: WorkflowNavigationService
  ) { }

  ngOnInit(): void {
    this.organizationId = this.keycloakStateService.getOrganizationId() || '';
    this.importedBy = this.getImportedByFromKeycloak();
    this.isCreate = Boolean(this.route.snapshot.data.create);
    this.existingAssetSourceId =
      this.route.snapshot.params.assetsourceId ||
      this.route.snapshot.params.assetSourceId ||
      null;

    this.buildForm();

    if (!this.isCreate) {
      this.loadExistingSystemAssetSource();
    }
  }

  get configItems(): FormArray {
    return this.form.get('configItems') as FormArray;
  }

  get pageTitle(): string {
    return this.isCreate ? 'Add System Asset Source' : 'Update System Asset Source';
  }

  get selectedSourceLabel(): string {
    const source = this.normalize(this.form?.get('assetSource')?.value);
    return this.sourceCatalog.find(item => item.value === source)?.label || '';
  }

  get selectedSourceTypeLabel(): string {
    const sourceType = this.normalize(this.form?.get('assetSourceType')?.value);
    return this.assetSourceTypeOptions.find(item => item.value === sourceType)?.label || '';
  }

  get canContinueFromStepThree(): boolean {
    if (this.importChoice === 'later') {
      return true;
    }
    return this.importChoice === 'now' && this.importCompleted;
  }

private getConnectionDetailsForUpdate(): AssetSourceConfigurationItem[] {
  return this.configItems.controls.map(control => ({
    assetSourceConfigurationId: String(
      control.get('configurationId')?.value ?? ''
    ),
    attributeName: String(
      control.get('attributeName')?.value ?? ''
    ),
    attributeValue: String(
      control.get('attributeValue')?.value ?? ''
    )
  }));
}

  private buildForm(): void {
    this.form = this.fb.group({
      assetSourceId: [this.existingAssetSourceId],
      assetSourceName: ['', Validators.required],
      assetType: ['system', Validators.required],
      assetSourceType: ['', Validators.required],
      assetSource: ['', Validators.required],
      url: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+/i)]],
      configItems: this.fb.array([])
    });

    this.form.get('url')?.valueChanges.subscribe(() => this.resetConnectionTest());
  }

  private getImportedByFromKeycloak(): string {
    try {
      const keycloak = this.keycloakService.getKeycloakInstance();
      const token = (keycloak?.tokenParsed || {}) as Record<string, unknown>;
      const username = token['preferred_username'] || token['name'] || token['email'];
      return username ? String(username) : '';
    } catch {
      return '';
    }
  }

  onAssetSourceTypeChange(): void {
    const selectedType = this.normalize(this.form.get('assetSourceType')?.value);
    this.assetSourceOptions = this.sourceCatalog
      .filter(item => item.assetSourceType === selectedType)
      .map(item => ({ value: item.value, label: item.label }));

    this.form.get('assetSource')?.setValue('', { emitEvent: false });
    this.clearConfigItems();
    this.resetConnectionTest();
    this.resetImportState();
  }

  onAssetSourceChange(): void {
    const source = this.normalize(this.form.get('assetSource')?.value);
    const catalogItem = this.sourceCatalog.find(item => item.value === source);

    this.clearConfigItems();
    if (catalogItem) {
      catalogItem.configItems.forEach(item => this.configItems.push(
        this.fb.group({
          attributeName: [item.attributeName],
          attributeValue: ['', Validators.required],
          protection: [item.protection]
        })
      ));
    }

    this.showPasswordMap = this.configItems.controls.map(() => false);
    this.resetConnectionTest();
    this.resetImportState();
  }

  onConnectionDetailsChanged(): void {
    this.resetConnectionTest();
  }

  private resetConnectionTest(): void {
    this.isConnectionTestSuccessful = false;
    this.connectionTestMessage = '';
    this.connectionTestError = false;
  }

  private resetImportState(): void {
    this.importChoice = null;
    this.importCompleted = false;
    this.importMessage = '';
    this.importedAssets = [];
  }

  togglePasswordVisibility(index: number): void {
    this.showPasswordMap[index] = !this.showPasswordMap[index];
  }

  private clearConfigItems(): void {
    if (this.form) {
      this.configItems.clear();
    }
    this.showPasswordMap = [];
  }

  private normalize(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private getConnectionDetails(): AssetSourceConfigurationItem[] {
    return this.configItems.controls.map((control: AbstractControl) => ({
      attributeName: String(control.get('attributeName')?.value ?? ''),
      attributeValue: String(control.get('attributeValue')?.value ?? '')
    }));
  }

  private getStepControls(step: number): AbstractControl[] {
    if (step === 1) {
      return [
        this.form.get('assetSourceName')!,
        this.form.get('assetSourceType')!,
        this.form.get('assetSource')!
      ];
    }

    return [this.form.get('url')!, ...this.configItems.controls];
  }

  private validateStep(step: number): boolean {
    const controls = this.getStepControls(step);
    controls.forEach(control => control.markAllAsTouched());
    return controls.every(control => control.valid);
  }

  goToStep(step: number): void {
    if (step < this.currentStep || !this.isCreate) {
      this.currentStep = step;
      return;
    }

    if (this.currentStep === 1 && step > 1) {
      this.goToStepTwo();
      return;
    }
    if (this.currentStep === 2 && step > 2) {
      this.goToStepThree();
      return;
    }
    if (this.currentStep === 3 && step > 3 && this.canContinueFromStepThree) {
      this.currentStep = 4;
    }
  }

  goToStepTwo(): void {
    if (!this.validateStep(1)) {
      return;
    }
    this.currentStep = 2;
  }

  goToStepThree(): void {
    if (!this.validateStep(2)) {
      this.connectionTestMessage = 'Enter a valid connection URL and complete all required connection details.';
      this.connectionTestError = true;
      return;
    }
    if (!this.isConnectionTestSuccessful) {
      this.connectionTestMessage = 'Test the connection successfully before continuing.';
      this.connectionTestError = true;
      return;
    }
    this.currentStep = 3;
  }

  goToReview(): void {
    if (this.canContinueFromStepThree) {
      this.currentStep = 4;
    }
  }

  isConnectionConfigurationValid(): boolean {
    return Boolean(
      this.form.get('url')?.valid &&
      this.configItems.length > 0 &&
      this.configItems.controls.every(control => control.valid)
    );
  }

  testConnection(): void {
    if (!this.isConnectionConfigurationValid()) {
      this.validateStep(2);
      this.connectionTestMessage = 'Enter a valid connection URL and complete all required connection details.';
      this.connectionTestError = true;
      return;
    }

    this.testingConnection = true;
    this.resetConnectionTest();

    this.systemAssetSourceService.TestAssetSourceConnection(
      this.normalize(this.form.get('assetSource')?.value),
      String(this.form.get('url')?.value ?? '').trim(),
      this.getConnectionDetails()
    ).subscribe({
      next: response => {
        const message = this.extractMessage(response);
        this.connectionTestMessage = message || 'Unexpected response from the connection test.';
        this.isConnectionTestSuccessful = /successful/i.test(this.connectionTestMessage);
        this.connectionTestError = !this.isConnectionTestSuccessful;
        this.testingConnection = false;
      },
      error: error => {
        this.connectionTestMessage = this.extractMessage(error) || 'Connection test failed. Check the URL and credentials.';
        this.connectionTestError = true;
        this.isConnectionTestSuccessful = false;
        this.testingConnection = false;
      }
    });
  }

  chooseImport(choice: 'now' | 'later'): void {
    this.importChoice = choice;
    this.importCompleted = false;
    this.importMessage = '';
    this.importedAssets = [];
  }

  importAssets(): void {
    if (this.importChoice !== 'now' || !this.isConnectionTestSuccessful || this.importingAssets) {
      return;
    }

    this.importingAssets = true;
    const value = this.form.getRawValue();

    this.systemAssetSourceService.ImportSystemAssetsDuringSetup(
      this.organizationId,
      String(value.assetSourceName).trim(),
      this.normalize(value.assetSourceType),
      this.normalize(value.assetSource),
      this.importedBy,
      String(value.url).trim(),
      this.getConnectionDetails(),
      this.keycloakStateService.getUserId() || ''
    ).subscribe({
      next: response => {
        this.importMessage = this.extractMessage(response) || 'Import request completed.';

        this.systemAssetSourceService.ReadSystemAssetsFromStagingTable(
          this.keycloakStateService.getUserId() || '',
          String(value.assetSourceName).trim()
        ).subscribe({
          next: assets => {
            this.importedAssets = Array.isArray(assets) ? assets : [];
            this.importCompleted = true;
            this.importingAssets = false;

            if (!this.importMessage) {
              this.importMessage = `Loaded ${this.importedAssets.length} imported assets.`;
            }
          },
          error: error => {
            this.importingAssets = false;
            this.importCompleted = false;
            this.importMessage =
              this.extractMessage(error) ||
              'Assets were imported, but the staging table could not be read.';
          }
        });
      },
      error: error => {
        this.importingAssets = false;
        this.importCompleted = false;
        this.importMessage =
          this.extractMessage(error) ||
          'Asset import failed. Check the connection and try again.';
      }
    });
  }

  submitUpdate(): void {
    if (this.isCreate || this.loading) {
      return;
    }

    this.form.markAllAsTouched();

    const value = this.form.getRawValue();
    const assetSourceId = String(
      value.assetSourceId || this.existingAssetSourceId || ''
    );
    const assetSourceName = String(value.assetSourceName ?? '').trim();
    const url = String(value.url ?? '').trim();

    if (this.form.invalid) {
      return;
    }

    if (!assetSourceId) {
      this.openErrorDialog('Asset Source ID is missing.');
      return;
    }


    this.loading = true;

    const sourceDetailsUpdate$ =
      this.systemAssetSourceService
        .UpdateSystemAssetSourceDetails(
          assetSourceId,
          assetSourceName
        )
        .pipe(
          map(response => ({
            succeeded: true as const,
            response
          })),
          catchError(error =>
            of({
              succeeded: false as const,
              error
            })
          )
        );

    const connectionDetailsUpdate$ =
      this.systemAssetSourceService
        .UpdateSystemAssetSourceConnectionDetails(
          assetSourceId,
          url,
          this.getConnectionDetailsForUpdate()
        )
        .pipe(
          map(response => ({
            succeeded: true as const,
            response
          })),
          catchError(error =>
            of({
              succeeded: false as const,
              error
            })
          )
        );

    forkJoin({
      sourceDetails: sourceDetailsUpdate$,
      connectionDetails: connectionDetailsUpdate$
    }).subscribe({
      next: ({ sourceDetails, connectionDetails }) => {
        this.loading = false;

        const errors: string[] = [];

        if ('error' in sourceDetails) {
          errors.push(
            `Source details: ${this.extractMessage(sourceDetails.error) || 'Update failed.'
            }`
          );
        }

        if ('error' in connectionDetails) {
          errors.push(
            `Connection details: ${this.extractMessage(connectionDetails.error) || 'Update failed.'
            }`
          );
        }

        if (errors.length > 0) {
          this.openErrorDialog(
            `The asset source was not fully updated. ${errors.join(' ')}`
          );
          return;
        }

        if ('response' in sourceDetails && 'response' in connectionDetails) {
          const sourceMessage = this.extractMessage(sourceDetails.response);
          const connectionMessage = this.extractMessage(connectionDetails.response);

          this.completionMessage =
            [sourceMessage, connectionMessage].filter(Boolean).join(' ') ||
            'System asset source and connection details updated successfully.';

          this.openSuccessDialog(false);
        }
      }
    });
  }

  finishSetup(): void {
    if (!this.isCreate || !this.validateStep(1) || !this.validateStep(2) || !this.isConnectionTestSuccessful) {
      return;
    }

    if (!this.canContinueFromStepThree) {
      this.currentStep = 3;
      return;
    }

    this.loading = true;
    const value = this.form.getRawValue();
    const isAssetImport = this.importChoice === 'now' && this.importCompleted ? 'true' : 'false';

    this.systemAssetSourceService.CompleteSystemAssetCapture(
      String(value.assetSourceName).trim(),
      this.normalize(value.assetType || 'system'),
      this.normalize(value.assetSourceType),
      this.normalize(value.assetSource),
      this.organizationId,
      String(value.url).trim(),
      this.getConnectionDetails(),
      isAssetImport,
      this.keycloakStateService.getUserId() || ''
    ).subscribe({
      next: response => {
        this.loading = false;
        this.completionMessage =
          this.extractMessage(response) ||
          'Asset source setup completed successfully.';

        this.openSuccessDialog(true);
      },
      error: error => {
        this.loading = false;
        this.openErrorDialog(
          this.extractMessage(error) ||
          'Unable to complete system asset source setup.'
        );
      }
    });
  }

  private loadExistingSystemAssetSource(): void {
    if (!this.existingAssetSourceId) {
      this.openErrorDialog('Asset Source ID is missing.');
      return;
    }

    this.loading = true;

    forkJoin({
      sourceResponse: this.systemAssetSourceService.FetchSystemAssetSourceDetails(
        this.existingAssetSourceId
      ),
      connectionResponse:
        this.systemAssetSourceService.fetchSystemAssetSourceConnectionDetails(
          this.existingAssetSourceId
        )
    }).subscribe({
      next: ({ sourceResponse, connectionResponse }) => {
        // The source-details API may return an array.
        const source: any = Array.isArray(sourceResponse)
          ? sourceResponse[0]
          : sourceResponse;

        const connection: any = Array.isArray(connectionResponse)
          ? connectionResponse[0]
          : connectionResponse;

        if (!source || !connection) {
          this.loading = false;
          this.openErrorDialog(
            'System asset source or connection details were not found.'
          );
          return;
        }

        const type = this.normalize(source.assetSourceType);
        const assetSource = this.normalize(source.assetSource);

        this.assetSourceOptions = this.sourceCatalog
          .filter(item => item.assetSourceType === type)
          .map(item => ({
            value: item.value,
            label: item.label
          }));

        const configuration: any[] =
          connection.assetSourceConfigurationAttributeDTOs ?? [];

        /*
         * Prefer the top-level configuration ID.
         * The supplied API example has this field as null, so fall back
         * to the first configuration attribute's ID.
         */
        

        this.form.patchValue(
          {
            assetSourceId:
              source.assetSourceId || this.existingAssetSourceId,
            assetSourceName: source.assetSourceName ?? '',
            assetType: this.normalize(source.assetType || 'system'),
            assetSourceType: type,
            assetSource,
            url: connection.url ?? ''
          },
          { emitEvent: false }
        );

        this.form.get('assetSourceType')?.disable({ emitEvent: false });
        this.form.get('assetSource')?.disable({ emitEvent: false });

        this.clearConfigItems();

        configuration.forEach(item => {
          const attributeName = String(item.attributeName ?? '');

          const catalogConfig = this.sourceCatalog
            .find(entry => entry.value === assetSource)
            ?.configItems.find(
              config =>
                config.attributeName.toLowerCase() ===
                attributeName.toLowerCase()
            );

          this.configItems.push(
            this.fb.group({
              configurationId: [
                item.assetSourceConfigurationId ?? null
              ],
              attributeName: [attributeName],
              attributeValue: [
                item.attributeValue ?? '',
                Validators.required
              ],
              protection: [
                item.protection ??
                catalogConfig?.protection ??
                /password|token|secret/i.test(attributeName)
              ]
            })
          );
        });

        this.showPasswordMap = this.configItems.controls.map(() => false);
        this.loading = false;
      },
      error: error => {
        this.loading = false;

        this.openErrorDialog(
          this.extractMessage(error) ||
          'Unable to fetch system asset source and connection details.'
        );
      }
    });
  }

  cancel(): void {
    // this.router.navigate(['asset-management/auth-tools/system-asset-capture'], {
    //   queryParams: this.isCreate ? { isAdd: true } : undefined
    // });
     void this.workflowNavigation.navigateToWorkflow(
    'systemAssetCapture'
  );
  }

  navigateToSystemAssetCapture(): void {
  void this.workflowNavigation.navigateToWorkflow(
    'systemAssetCapture'
  );
}

  openSuccessDialog(isCreate: boolean): void {
    const dialogRef = this.dialog.open(SuccessDialogComponent, {
      width: '360px',
      data: {
        isCreate,
        isCreateUpdate: true,
        title: 'System Asset Source',
        message: this.completionMessage
      }
    });

    dialogRef.afterClosed().subscribe(() => {
       void this.workflowNavigation.navigateToWorkflow(
    'systemAssetCapture'
  );
    });
  }

  private openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: '360px',
      data: { errorMessage }
    });
  }

  private extractMessage(responseOrError: any): string {
    if (typeof responseOrError === 'string') {
      return responseOrError;
    }

    const candidates = [
      responseOrError?.error?.message,
      responseOrError?.error,
      responseOrError?.message,
      responseOrError?.statusText
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
      if (candidate && typeof candidate === 'object') {
        const firstString = Object.values(candidate).find(value => typeof value === 'string' && value.trim());
        if (typeof firstString === 'string') {
          return firstString;
        }
      }
    }

    // Some legacy error responses use the message itself as the JSON key.
    if (responseOrError && typeof responseOrError === 'object') {
      const keys = Object.keys(responseOrError);
      if (keys.length === 1 && !['error', 'message', 'status', 'statusText'].includes(keys[0])) {
        return keys[0];
      }
    }

    return '';
  }
}
